import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

interface FeatureGateProps {
  /** Feature flag key conforme tabela feature_flags. */
  flag: string;
  /** Valor padrão quando a flag não existe na tabela (default: true). */
  defaultEnabled?: boolean;
  /** Renderizado quando a flag está desligada. Default: null (some). */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Esconde/mostra parte da UI conforme uma feature flag global.
 * Use para botões, cards, painéis isolados. Para rotas inteiras, use FeatureRoute.
 */
export function FeatureGate({ flag, defaultEnabled = true, fallback = null, children }: FeatureGateProps) {
  const { isEnabled, loading } = useFeatureFlags();
  if (loading) return null;
  if (!isEnabled(flag, defaultEnabled)) return <>{fallback}</>;
  return <>{children}</>;
}

interface FeatureRouteProps {
  flag: string;
  defaultEnabled?: boolean;
  /** Título mostrado na tela "desativada". */
  title?: string;
  children: ReactNode;
}

/** Wrapper para rotas inteiras — mostra uma tela amigável quando a feature está OFF. */
export function FeatureRoute({ flag, defaultEnabled = true, title = "Recurso temporariamente indisponível", children }: FeatureRouteProps) {
  const { isEnabled, loading } = useFeatureFlags();
  if (loading) return null;
  if (isEnabled(flag, defaultEnabled)) return <>{children}</>;
  return (
    <div className="min-h-dvh bg-background flex flex-col items-center justify-center px-6 text-center">
      <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
        <Lock className="w-7 h-7 text-primary" />
      </div>
      <h1 className="text-xl font-display text-foreground mb-2">{title}</h1>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        Esta funcionalidade está desativada no momento. Tente novamente mais tarde — nossa equipe está cuidando disso.
      </p>
      <Link to="/" className="text-sm text-primary underline">Voltar ao início</Link>
    </div>
  );
}

export default FeatureGate;
